/* Shared UI primitives for the Meridian console.
   Deliberately neutral: this product must look like it belongs to no vertical. */

export const T = {
  bg: "#0b0d10",
  panel: "#12151a",
  panel2: "#171b21",
  line: "rgba(255,255,255,0.08)",
  line2: "rgba(255,255,255,0.14)",
  text: "#e8eaed",
  dim: "rgba(232,234,237,0.62)",
  dim2: "rgba(232,234,237,0.40)",
  accent: "#5b86ff",
  ok: "#2ec27e",
  warn: "#e8b339",
  crit: "#ff6b5e",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

export const Panel = ({ title, meta, children, actions, testId, className = "" }) => (
  <section className={`mrd-panel ${className}`} data-testid={testId}>
    {(title || actions) && (
      <header className="mrd-panel-head">
        <div>
          {title && <h2 className="mrd-panel-title">{title}</h2>}
          {meta && <div className="mrd-panel-meta">{meta}</div>}
        </div>
        {actions && <div className="mrd-panel-actions">{actions}</div>}
      </header>
    )}
    <div className="mrd-panel-body">{children}</div>
  </section>
);

export const Btn = ({ children, onClick, tone = "default", disabled, testId, size = "md" }) => (
  <button className={`mrd-btn mrd-btn-${tone} mrd-btn-${size}`} onClick={onClick} disabled={disabled}
          data-testid={testId}>
    {children}
  </button>
);

const TONES = {
  verified: T.ok, active: T.ok, clean: T.ok, open: T.accent, ALLOW: T.ok,
  ALLOW_WITH_CONTROLS: T.warn, REVIEW: T.warn, BLOCK: T.crit,
  review: T.warn, pending: T.warn, elevated: T.warn, high: T.crit,
  critical: T.crit, exceptions: T.crit, rejected: T.crit, suspended: T.crit,
  medium: T.warn, low: T.dim, standard: T.dim, resolved: T.ok, settled: T.dim,
};

export const Badge = ({ children, tone, testId }) => {
  const color = TONES[tone ?? children] || T.dim;
  return (
    <span className="mrd-badge" data-testid={testId}
          style={{ color, borderColor: color, background: `${color}14` }}>
      {children}
    </span>
  );
};

export const Kv = ({ label, value, mono = false, testId }) => (
  <div className="mrd-kv" data-testid={testId}>
    <div className="mrd-kv-label">{label}</div>
    <div className="mrd-kv-value" style={mono ? { fontFamily: T.mono, fontSize: 12.5 } : undefined}>{value}</div>
  </div>
);

export const Stat = ({ label, value, sub, tone, testId }) => (
  <div className="mrd-stat" data-testid={testId}>
    <div className="mrd-stat-label">{label}</div>
    <div className="mrd-stat-value" style={tone ? { color: TONES[tone] || T.text } : undefined}>{value}</div>
    {sub && <div className="mrd-stat-sub">{sub}</div>}
  </div>
);

export const Table = ({ columns, rows, empty = "Nothing yet", testId }) => (
  <div className="mrd-table-wrap" data-testid={testId}>
    <table className="mrd-table">
      <thead>
        <tr>{columns.map((c) => <th key={c.key} style={c.align ? { textAlign: c.align } : undefined}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="mrd-empty">{empty}</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={r.__key || i}>
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align || "left", fontFamily: c.mono ? T.mono : undefined,
                                       fontSize: c.mono ? 12.5 : undefined }}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Code = ({ value, testId }) => (
  <pre className="mrd-code" data-testid={testId}>{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</pre>
);

export const styles = `
.mrd-root { min-height: 100vh; background: ${T.bg}; color: ${T.text};
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, sans-serif; font-feature-settings: "cv11","ss01";
  -webkit-font-smoothing: antialiased; }
.mrd-root * { box-sizing: border-box; }
.mrd-top { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between;
  gap: 24px; padding: 0 24px; height: 60px; background: rgba(11,13,16,0.92); backdrop-filter: blur(10px);
  border-bottom: 1px solid ${T.line}; }
.mrd-brand { display: flex; align-items: center; gap: 12px; }
.mrd-mark { width: 26px; height: 26px; border-radius: 7px; background: ${T.accent}; position: relative; }
.mrd-mark::after { content: ""; position: absolute; inset: 7px; border-radius: 2px; border: 2px solid ${T.bg};
  border-top-color: transparent; border-right-color: transparent; transform: rotate(-45deg) translate(1px,-1px); }
.mrd-brand-name { font-size: 15px; font-weight: 600; letter-spacing: -0.01em; }
.mrd-brand-sub { font-family: ${T.mono}; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: ${T.dim2}; padding-left: 12px; margin-left: 4px; border-left: 1px solid ${T.line}; }
.mrd-top-right { display: flex; align-items: center; gap: 12px; }
.mrd-layout { display: grid; grid-template-columns: 216px 1fr; align-items: start; }
.mrd-rail { position: sticky; top: 60px; height: calc(100vh - 60px); border-right: 1px solid ${T.line};
  padding: 18px 12px; display: flex; flex-direction: column; gap: 2px; }
.mrd-rail-label { font-family: ${T.mono}; font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
  color: ${T.dim2}; padding: 14px 10px 8px; }
.mrd-rail-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; width: 100%;
  padding: 9px 10px; border: none; background: transparent; border-radius: 7px; color: ${T.dim};
  font: inherit; font-size: 13.5px; cursor: pointer; text-align: left; transition: background-color .15s ease, color .15s ease; }
.mrd-rail-item:hover { background: rgba(255,255,255,0.04); color: ${T.text}; }
.mrd-rail-item.is-active { background: rgba(91,134,255,0.12); color: ${T.text}; box-shadow: inset 2px 0 0 ${T.accent}; }
.mrd-rail-count { font-family: ${T.mono}; font-size: 11px; color: ${T.dim2}; }
.mrd-main { padding: 28px 32px 64px; display: flex; flex-direction: column; gap: 20px; max-width: 1400px; }
.mrd-h1 { font-size: 22px; font-weight: 600; letter-spacing: -0.02em; margin: 0; }
.mrd-sub { font-size: 13.5px; color: ${T.dim}; margin: 6px 0 0; max-width: 76ch; }
.mrd-panel { background: ${T.panel}; border: 1px solid ${T.line}; border-radius: 12px; overflow: hidden; }
.mrd-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  padding: 16px 18px; border-bottom: 1px solid ${T.line}; }
.mrd-panel-title { font-size: 14px; font-weight: 600; margin: 0; letter-spacing: -0.01em; }
.mrd-panel-meta { font-size: 12px; color: ${T.dim2}; margin-top: 4px; }
.mrd-panel-actions { display: flex; gap: 8px; flex-shrink: 0; }
.mrd-panel-body { padding: 18px; }
.mrd-panel-body.flush { padding: 0; }
.mrd-grid { display: grid; gap: 16px; }
.mrd-grid-2 { grid-template-columns: repeat(2, minmax(0,1fr)); }
.mrd-grid-3 { grid-template-columns: repeat(3, minmax(0,1fr)); }
.mrd-grid-4 { grid-template-columns: repeat(4, minmax(0,1fr)); }
.mrd-btn { font: inherit; font-size: 12.5px; font-weight: 500; border-radius: 7px; cursor: pointer;
  border: 1px solid ${T.line2}; background: rgba(255,255,255,0.04); color: ${T.text};
  transition: background-color .15s ease, border-color .15s ease, opacity .15s ease; }
.mrd-btn-md { padding: 8px 14px; } .mrd-btn-sm { padding: 5px 10px; font-size: 11.5px; }
.mrd-btn:hover:not(:disabled) { background: rgba(255,255,255,0.08); }
.mrd-btn:disabled { opacity: .45; cursor: not-allowed; }
.mrd-btn-primary { background: ${T.accent}; border-color: ${T.accent}; color: #08101f; font-weight: 600; }
.mrd-btn-primary:hover:not(:disabled) { background: #6f95ff; }
.mrd-btn-danger { background: rgba(255,107,94,0.12); border-color: rgba(255,107,94,0.5); color: ${T.crit}; }
.mrd-badge { display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 999px;
  border: 1px solid; font-family: ${T.mono}; font-size: 10.5px; letter-spacing: 0.06em; text-transform: uppercase; }
.mrd-kv { display: flex; flex-direction: column; gap: 4px; }
.mrd-kv-label { font-family: ${T.mono}; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: ${T.dim2}; }
.mrd-kv-value { font-size: 13.5px; }
.mrd-stat { padding: 16px 18px; border: 1px solid ${T.line}; border-radius: 10px; background: ${T.panel2}; }
.mrd-stat-label { font-family: ${T.mono}; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: ${T.dim2}; }
.mrd-stat-value { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin-top: 8px; }
.mrd-stat-sub { font-size: 12px; color: ${T.dim2}; margin-top: 4px; }
.mrd-table-wrap { overflow-x: auto; }
.mrd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.mrd-table th { text-align: left; font-family: ${T.mono}; font-size: 10px; letter-spacing: 0.14em;
  text-transform: uppercase; color: ${T.dim2}; font-weight: 500; padding: 12px 14px;
  border-bottom: 1px solid ${T.line}; white-space: nowrap; }
.mrd-table td { padding: 12px 14px; border-bottom: 1px solid rgba(255,255,255,0.05); vertical-align: top; }
.mrd-table tr:last-child td { border-bottom: none; }
.mrd-table tbody tr:hover { background: rgba(255,255,255,0.02); }
.mrd-empty { color: ${T.dim2}; font-size: 12.5px; padding: 20px 14px; }
.mrd-code { font-family: ${T.mono}; font-size: 11.5px; line-height: 1.55; color: ${T.dim};
  background: #0d1014; border: 1px solid ${T.line}; border-radius: 8px; padding: 14px;
  max-height: 340px; overflow: auto; margin: 0; white-space: pre-wrap; word-break: break-word; }
.mrd-field { display: flex; flex-direction: column; gap: 6px; }
.mrd-field label { font-family: ${T.mono}; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: ${T.dim2}; }
.mrd-input, .mrd-select { font: inherit; font-size: 13px; color: ${T.text}; background: #0d1014;
  border: 1px solid ${T.line2}; border-radius: 7px; padding: 8px 10px; }
.mrd-input:focus, .mrd-select:focus { outline: none; border-color: ${T.accent}; }
.mrd-check { display: flex; align-items: center; gap: 8px; font-size: 13px; color: ${T.dim}; }
.mrd-steps { display: flex; flex-direction: column; }
.mrd-step { display: grid; grid-template-columns: 30px 1fr auto; gap: 14px; align-items: start;
  padding: 14px 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
.mrd-step:last-child { border-bottom: none; }
.mrd-step-n { font-family: ${T.mono}; font-size: 11px; color: ${T.dim2}; padding-top: 2px; }
.mrd-step-title { font-size: 13.5px; font-weight: 500; }
.mrd-step-detail { font-size: 12.5px; color: ${T.dim}; margin-top: 4px; font-family: ${T.mono}; }
.mrd-banner { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 10px;
  font-size: 13px; border: 1px solid; }
`;
